import { useUser } from "@/hooks/useUser";
import { SpecialEntity, SpecialRight } from "@/lib/types";

interface PermissionCheckerProps {
  entity: SpecialEntity;
  rights: SpecialRight[];
  children: React.ReactNode;
}

export const apiEndpointBuilder = (entity: SpecialEntity, right: SpecialRight) => {
  if (right === "List") return `v1/${entity}`;
  if (right === "Add") return `v1/${entity}/Add`;
  if (right === "Delete") return `v1/${entity}/Delete`;
  if (right === "GetDetails") return `v1/${entity}/GetDetails`;
  if (right === "Update") return `v1/${entity}/Update`;
  return "";
};

export const useHasPermission = (entity: SpecialEntity, rights: SpecialRight[]) => {
  const { user, isLoading } = useUser();
  if (isLoading) return false;

  return user?.apiRights.some((apiRight) =>
    rights.some((right) => {
      if (right === "Delete") {
        return (
          apiRight.apiEndPoint === apiEndpointBuilder(entity, right) ||
          apiRight.apiEndPoint === `${apiEndpointBuilder(entity, right)}/{Id}` ||
          // Check pattern {MyCustomId} at the end of the string
          apiRight.apiEndPoint.startsWith(`${apiEndpointBuilder(entity, right)}/{`)
        );
      }
      return apiRight.apiEndPoint === apiEndpointBuilder(entity, right);
    })
  );
};

export const withPermissionHelper = (entity: SpecialEntity, rights: SpecialRight[]) => {
  const hasPermission = useHasPermission(entity, rights);
  return (e: any) => {
    if (hasPermission) return e;
    return null;
  };
};

export const withMultiplePermissionHelper = (entities: { entity: SpecialEntity; rights: SpecialRight[] }[]) => {
  return (e: any) => {
    if (entities.some((entity) => useHasPermission(entity.entity, entity.rights))) return e;
    return null;
  };
};

export const withPermissionHoc = (entity: SpecialEntity, rights: SpecialRight[]) => {
  return (props: PermissionCheckerProps) => {
    const hasPermission = useHasPermission(entity, rights);
    return hasPermission ? <>{props.children}</> : null;
  };
};

export const PermissionChecker2: React.FC<PermissionCheckerProps> = ({ entity, rights, children }) => {
  const hasPermission = useHasPermission(entity, rights);
  return hasPermission ? <>{children}</> : null;
};

export const PermissionCheckerStandaloneEndpoint: React.FC<{
  apiEndpoint: string;
  children: React.ReactNode;
}> = ({ apiEndpoint, children }) => {
  const { user, isLoading } = useUser();
  if (isLoading) return null;
  return user?.apiRights.some((apiRight) => apiRight.apiEndPoint === apiEndpoint) ? <>{children}</> : null;
};
